import { CredentialType, type UserCredential } from '@/portalApi'

/**
 * Whether holding this credential means MFA is on. The API defines every type but the
 * password as a second factor, backup codes included, and says to expect new types; so
 * this excludes the password rather than listing the rest, and a new type counts as the
 * backend already counts it.
 */
export const isSecondFactor = (credential: UserCredential): boolean =>
  credential.type !== CredentialType.password

/**
 * Whether the credentials include a second factor. `undefined` when they were not read,
 * which the API reports as null: unknown, which a caller must not show as MFA being off.
 */
export const mfaEnabledFrom = (
  credentials: UserCredential[] | null | undefined,
): boolean | undefined => credentials?.some(isSecondFactor)

/**
 * The identity fields every user shape carries. Structural rather than tied to
 * `User` or `UserDetail`, so the same helper serves both, plus the token claims.
 */
type NamedUser = {
  given_name?: string
  family_name?: string
}

/**
 * A user's display name, built from the name parts the API returns.
 *
 * Both parts are optional on every user shape, so missing ones are dropped
 * rather than rendered as "undefined" or left as a stray separator. Returns an
 * empty string when neither is set; callers decide what to show instead.
 */
export const fullName = (user: NamedUser | undefined): string =>
  [user?.given_name, user?.family_name].filter((part) => !!part).join(' ')
